import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  Grid,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { fetchGet, fetchPut, fetchDelete } from '../../../../../helpers/fetching';
import { setSnackbar } from '../../../../../store';

const useStyles = makeStyles((theme) => ({
  titleSettingsCard: {
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
    fontSize: '1.1rem',
  },
  listContainer: {
    marginTop: 16,
    padding: 16,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
  },
  listTitle: {
    fontSize: theme.fonts.responsiveMedium,
    letterSpacing: '0.1rem',
    color: theme.palette.texts.primary,
    fontWeight: 'bold',
  },
}));

export default function OptedOutPatients() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    const response = await fetchGet('patients_opted_out');
    if (response.success) {
      setPatients(response.data);
    } else {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        })
      );
    }
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  async function addPatient() {
    const trimmedId = patientId.trim();
    if (!trimmedId) return;

    setAdding(true);
    const response = await fetchPut('patients_opted_out', { patient_id: trimmedId });
    if (response.success) {
      setPatientId('');
      await fetchPatients();
      dispatch(
        setSnackbar({
          msg: 'Settings.patientsOptOutAdded',
          severity: 'success',
        })
      );
    } else {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        })
      );
    }
    setAdding(false);
  }

  async function removePatient(id) {
    const response = await fetchDelete('patients_opted_out', { patient_id: id });
    if (response.success) {
      setPatients((prev) => prev.filter((p) => p !== id));
      dispatch(
        setSnackbar({
          msg: 'Settings.patientsOptOutRemoved',
          severity: 'success',
        })
      );
    } else {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        })
      );
    }
  }

  return (
    <Grid container direction='column' spacing={2}>
      <Grid item xs={12}>
        <Card elevation={1}>
          <CardHeader
            disableTypography
            title={
              <Typography className={classes.titleSettingsCard} variant='h6'>
                {t('Settings.optingOutPatients')}
              </Typography>
            }
          />
          <CardContent style={{ paddingTop: 0 }}>
            <Grid container spacing={2} alignItems='center'>
              <Grid item xs>
                <TextField
                  label={t('Settings.patientOptOutId')}
                  value={patientId}
                  onChange={(e) => setPatientId(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addPatient()}
                  fullWidth
                />
              </Grid>
              <Grid item>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={addPatient}
                  disabled={adding || !patientId.trim()}>
                  {adding ? <CircularProgress size={24} /> : t('Settings.addPatientOptOut')}
                </Button>
              </Grid>
            </Grid>

            {loading ? (
              <CircularProgress style={{ marginTop: 16 }} />
            ) : patients.length === 0 ? (
              <Typography color='textSecondary' style={{ marginTop: 16 }}>
                {t('Settings.patientsOptOutEmpty')}
              </Typography>
            ) : (
              <div className={classes.listContainer}>
                <Typography className={classes.listTitle} variant='h6'>
                  {t('Settings.optingOutPatients')}
                </Typography>
                <List dense>
                  {patients.map((id) => (
                    <ListItem key={id} divider>
                      <ListItemText primary={id} />
                      <IconButton
                        edge='end'
                        aria-label='delete'
                        onClick={() => removePatient(id)}
                        size='small'>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </div>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
